import { themeColors } from "../../themeColors";
import usePlan from "@/hooks/usePlan";
import { useAgentBioLink } from "@/hooks/useAgentBioLink";
import { authorLinkAction } from "@/helpers/wantAdAuthorLink";
import AuthorMessageLink from "@/components/Common/AuthorMessageLink";
import BackButton from "@/components/Common/BackButton";
/**
 * @file   src\pages\WantadDetails.js
 * @brief  This file is responsible for showing want ad details page
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";
import MarketRestrictedModal from "@/components/Common/MarketRestrictedModal";
import { isMarketRestrictedWantAd } from "@/helpers/marketRestriction";
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import AdIntroIcon from "../../images/ad-intro-icon.svg";
import PlaceHolderIcon from "../../images/dpdown-placeholder-icon.svg";
import LakeViewIcon from "../../images/lake-view-icon.svg";
import ConsumerIcon from "../../images/consumer-icon.svg";
import FinancingIcon from "../../images/financing-icon.svg";
import DisplacedFired from "../../images/v2/displaced-fire.svg";
import PrResIcon from "../../images/primary-residence-icon.svg";
import MoreIcon from "../../images/more-icon.svg";
import SeoHead from "../../components/SeoHead";
import ReportWantAdModal from "../../components/MemberWantAd/ReportWantAdModal";
import ViewWantAdIntroductions from "../../components/Introduction/ViewWantAdIntroductions";
import ViewIntroductionModal from "../../components/Introduction/ViewIntroductionModal";
import ArchiveModal from "../../components/MyWantAd/ArchiveModal";
import PremiumPlanModal from "../../components/PlanUpgrade/PremiumPlanModal";
import PostedBanner from "@/components/PostedBanner";
import { useGitchaProOffer } from "@/hooks/useGitchaPro";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import {
  getPropertyIcon,
  getTooltipText,
  getCombinedText,
} from "../../helpers/getPropertyIcons";
import AgentProfileCard from "../../components/Common/AgentProfileCard";
import { useWantAdMlsMatches } from "@/hooks";
import useMatchingProgress, {
  matchesTabTitle,
} from "@/hooks/useMatchingProgress";
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  BUY,
  LONG_RENT,
  SHORT_RENT,
  LOT_LAND,
  WANT_AD_KEY,
  USER_INFO_KEY,
  INVENTORY_KEY,
  IS_MULTI_FAMILY,
  IS_LAND,
  INVESTOR_MEMBER_PLAN,
  USER_TYPE_AGENT,
  NICE_2_HAVES_CATEGORIES_MASTER_LIST,
  NICE_2_HAVES_LOT_LAND_CATEGORIES,
} from "../../constants/common";
import ShareYourAdModal from "../../components/MemberWantAd/v2/ShareYourAdModal";
import ShareMyWantAdSmallModal from "../../components/MemberWantAd/ShareMyWantAdSmallModal";
import {
  loadTimeLineHtml,
  getLocalStorage,
  setLocalStorage,
  getUserId,
  loggedInUser,
  getUserType,
  loadPreferredLocationHtml,
  locationDisplay,
  loadBuyerAgreementHtml,
  saveTutorialStatusToLocal,
  checkTutorialDisplayStatus,
} from "../../helpers/v2/common";
import { getWantAdDetailMeta } from "../../helpers/v2/wantAdDetailMeta";
import {
  resolveShareUrl,
  shouldCopyShareLinkDirectly,
} from "../../helpers/v2/shareWantad";
import { pageURLs } from "../../constants/pageURLs";
import {
  useNavigate,
  useSearchParams,
  NavLink,
  useParams,
  useLocation,
} from "react-router-dom";
import {
  favouriteWantAd,
  getAgentsList,
  getWantAdById,
  unFavouriteWantAd,
  archiveWantAd,
  unArchiveWantAd,
  deleteWantAd,
  getWantAdByIdPublic,
  getBoundaryCoordinates,
} from "../../services/v2/wantAdService";
import { convertToNumber } from "../../helpers/validations";
import { getRequestedConcessions } from "../../helpers/concessions";
import { resolveWantAdId, toWantAdId } from "../../helpers/wantAdId";
import { isPremiumAgentPlan } from "../../helpers/agentPlan";
import SendIntroduction from "../../components/Introduction/SendIntroduction";
import { isTourSlug, useTourSampleWantAd } from "@/components/MemberTour";
import SignedOutIntroCTA from "../../components/Introduction/SignedOutIntroCTA";
import WantContactPanel from "../../components/WantContactPanel";
import HearttIcon from "../../images/heart-icon.svg";
import HearttIconFilled from "../../images/heart-icon-filled.svg";
import { getIntroductions } from "../../services/introductionService";
import { getIntroductionDetaisById } from "../../services/introductionService";
import AddToClientWantAdModal from "../../components/MemberWantAd/AddToClientWantAdModal";
import { handleTextBoxChange } from "../../actions/common";
import { toast } from "react-toastify";
import DeleteModal from "../../components/Common/DeleteModal";
import CloseOutSheet from "@/components/CloseOutSheet";
import { getCloseOutContext } from "@/services/closeOutLoopService";
import {
  buildWantAdMustHavesHTML,
  loadInvestmentCriteria,
} from "../../helpers/v2/createWantAd";
import WantadDetailsSkeleton from "../WantadDetailsSkeleton";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import AgentProfileViewModal from "../../components/Introduction/AgentProfileViewModal";
import MinCapIcon from "../../images/minimum-icn.svg";
import userProfile from "../../appState/userProfile";
import { getUserPlanDetails } from "../../services/userprofileService";
import ShareCard from "../../components/MemberWantAd/v2/ShareCard";
import MapBoxComponent from "../../components/Common/MapboxComponent";
import {
  shareWantAdSocial,
  syndicateWantAdListing,
  unsyndicateWantAdListing,
} from "../../services/v2/wantAdService";
import NoWantAdFoundModal from "../../components/MemberWantAd/v2/NoWantAdFound";
import { useRecordWantAdView } from "@/hooks/useRecordWantAdView";
import { usePublicWantListingRedirect } from "@/hooks/usePublicWantListingRedirect";
import { useReadingTime } from "@/hooks/useReadingTime";
import { buildViewEntry } from "@/helpers/reachEntry";
import JoyRide, { STATUS } from "react-joyride";
import IntroductionTutorialIconStep1 from "../../images/tut-property-icon.svg";
import IntroductionTutorialIconStep2 from "../../images/tut-askingprice-icon.svg";
import IntroductionTutorialIconStep3 from "../../images/tut-custommsg-icon.svg";
import IntroductionTutorialIconStep4 from "../../images/tut-send-icon.svg";
import DocumentationTutorial from "../../components/Tutorial/DocumentationTutorial";
import { useAppConfig } from "@/appConfig";
import {
  SelectDropDown,
  MatchesSkeleton,
  SendMessage,
} from "@/components";
import { PageWrapper } from "@/components/PageWrapper";
import SyndicateListingModal from "../../components/MyWantAd/SyndicateListingModal";
import GitchaSyndication from "@/components/GitchaSyndication";
import WantListingRail from "@/components/WantListingRail";
import GitchaMemberBridge from "@/components/GitchaMemberBridge";
import GitchaShareAccess from "@/components/GitchaShareAccess";
import { ShareIconButton, ShareMenuItems, PublicShareMenu } from "@/components/GitchaShareMenu";
import { trackEvent } from "../../services/analytics";
import InventorySuccessModal from "../../components/Inventory/CreateInventory/InventorySuccessModal";
import useLoading from "@/hooks/useLoading";
import { copyToClipboard } from "@/helpers/clipboard";
import { useMLSAgentAtGitchaRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";
import useAgentGitchaRestrictions from "@/hooks/useAgentGitchaRestrictions";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import MatchesFeatureModal from "../../components/Common/MatchesFeatureModal";
import MlsMatchCard from "@/components/MlsListings/MlsMatchCard";
import useAiRanking, { aiKeyForListing } from "@/hooks/useAiRanking";
import "@/styles/desktop/general.scss";
import "@/styles/bls-detail-meta.scss";
import { openModal } from "@/reducers/modals";
import { withBase } from "@/helpers/basePath";
import {
  QUIET_PROGRESS_OPTIONS,
  useMarkMatchesSeen,
  useOnboardingProgress,
} from "@/hooks/useOnboardingProgress";

const SHARE_LINK_LOADING_KEY = "wantAdShareLink";

//Tutorial tooltip
const TutorialToolTip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  size,
  isLastStep,
  skipProps,
}) => (
  <>
    <div {...tooltipProps}>
      <DocumentationTutorial
        continuous={continuous}
        index={index}
        step={step}
        backProps={backProps}
        skipProps={skipProps}
        primaryProps={primaryProps}
        isLastStep={isLastStep}
        size={size}
        finalStepBtn={"Got It!"}
      />
    </div>
  </>
);

const tutorialSteps = [
  {
    target: "#propertySelection",
    disableBeacon: true,
    placement: "left",
    title: "Choose a property to share",
    icon: IntroductionTutorialIconStep1,
    content:
      "If you have multiple properties, you can swap out which one you want to introduce.",
    hideCloseButton: true,
    showProgress: true,
    showSkipButton: true,
  },
  {
    target: ".property-price",
    disableBeacon: true,
    placement: "bottom",
    title: "Adjust asking price",
    icon: IntroductionTutorialIconStep2,
    content:
      "This is just conversation starter. So try not to overthink it. You can always change it later.",
    hideCloseButton: true,
    showProgress: true,
    showSkipButton: true,
  },
  {
    target: ".property-message",
    disableBeacon: true,
    placement: "top",
    title: "Write a custom message",
    icon: IntroductionTutorialIconStep3,
    content:
      "This is your first chance to tell them why you think your property is a perfect match.",
    hideCloseButton: true,
    showProgress: true,
    showSkipButton: true,
  },
  {
    target: ".sendMessageBtn",
    disableBeacon: true,
    placement: "left",
    title: "Review and send",
    icon: IntroductionTutorialIconStep4,
    content:
      "You'll get another chance to review your introduction before you finalize and send.",
    hideCloseButton: true,
    showProgress: true,
  },
]

const OtherConcessionsSection = ({ adType, adDetails }) => {

  const shouldDisplayOtherConcessionsSection = (adType == BUY || adDetails?.prprtyType == IS_LAND) && adDetails?.isAgentCreated == 1;

  const otherConcessions = getRequestedConcessions(adDetails?.otherConcessions);

  if (!shouldDisplayOtherConcessionsSection || !otherConcessions) return null;

  return (
    <div className="buyer-info">
      <h2 className="wanted-details-head">
        Requested Concessions From Seller
      </h2>
      <ul>
        <li>{otherConcessions}</li>
      </ul>
    </div>
  )
}

const WantadDetails = (props) => {

  const navigate = useNavigate();

  const { openBio, bioHref, opensGitcha: opensGitchaBio } = useAgentBioLink();

  const location = useLocation();
  const isFromConfirmationPage = location.state?.isFromConfirmationPage;
  // Set when the agent arrived from a feed card while a listing was selected.
  const preselectListingKey = location.state?.preselectListingKey || null;

  const options = [
    { value: "most_relevant", label: "Highest match" },
    { value: "most_recent", label: "Recently listed" },
    { value: "oldest", label: "Longest on market" },
    { value: "price_high", label: "Price: high to low" },
    { value: "price_low", label: "Price: low to high" },
  ];

  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const id = searchParams.get("wantAdId") || params?.wantAdId;

  const [showShareModal, setShareModal] = useState(false);
  // GTCH-853: market has moved to the BLS. Null when not refused.
  const [marketRestrictedReason, setMarketRestrictedReason] = useState(null);
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);

  const [showSuccessModal, setSuccessModal] = useState(false);
  const [showSyndicateSuccessModal, setSyndicateSuccessModal] = useState(false);
  const [postedClosed, setPostedClosed] = useState(false);
  const [proReasonText, setProReasonText] = useState(null);
  const [showReportModal, setReportModal] = useState(false);
  const handleClose = () => setShareModal(false);
  const [showViewIntroductionModal, setViewIntroductionModal] = useState(false);
  const wantAd = getLocalStorage(WANT_AD_KEY);
  const userType = getUserType();

  //This variable is used to change intro title and description, if this page reaches from home feed.
  const isFromHomeFeed = id
    ? true
    : _.get(wantAd, "isFromHomeFeed", false);

  //This variable is used to change intro title and description, if this page reaches from all introductions.
  const isFromAllIntroduction = _.get(wantAd, "isFromAllIntroduction", false);
  // Whatever is in localStorage: the page URL's slug is written here too, so
  // this is only good for "has a want ad been selected". The id that is sent
  // anywhere is `wantAdId` below, taken from the loaded ad.
  const storedWantAdId = _.get(wantAd, "wantAdId", null);
  const introId = _.get(wantAd, "introId", null);
  const isEdit = _.get(wantAd, "isEdit", false);
  const loggedInUserId = getUserId();

  const queryClient = useQueryClient();

  const [pollMatchStatus, setPollMatchStatus] = useState(false);

  // Want-listing detail. Two fetch modes: the authenticated read once a
  // want-ad is selected and the viewer is signed in, the public read
  // otherwise. Both address params.wantAdId, as the previous code did.
  const useAuthedWantAdRead = !!(storedWantAdId && loggedInUserId);

  const tourWantAd = useTourSampleWantAd(params.wantAdId);
  const strayTourSample = isTourSlug(params.wantAdId) && !tourWantAd;

  useEffect(() => {
    if (strayTourSample) navigate(pageURLs.home, { replace: true });
  }, [strayTourSample, navigate]);

  const wantAdDetailsQuery = useQuery({
    enabled: !tourWantAd && !strayTourSample,
    queryKey: [
      "wantAdDetails",
      params.wantAdId,
      useAuthedWantAdRead,
      loggedInUserId || null,
    ],
    queryFn: async () => {
      const response = useAuthedWantAdRead
        ? await getWantAdById(params.wantAdId, loggedInUserId)
        : await getWantAdByIdPublic(params.wantAdId, loggedInUserId);
      const responseResult = _.get(response, "result", null);
      if (response.errorCode !== 0 || !responseResult) {
        const notFound = new Error("Want-listing not found");
        notFound.wantAdNotFound = true;
        throw notFound;
      }
      setLocalStorage(WANT_AD_KEY, {
        ...getLocalStorage(WANT_AD_KEY),
        wantAdType: responseResult?.adType,
        wantAdId: responseResult?.wantAdId,
      });
      return responseResult;
    },
    // Safety net for a missed SSE 'matchingComplete': re-poll the detail
    // status while the backend is still scoring, so the Matches tab never
    // stays stuck disabled. Replaces the former setInterval effect.
    refetchInterval: pollMatchStatus ? 6000 : false,
    // The previous implementation never retried; a transient network failure
    // left the skeleton up instead of showing the "not found" modal.
    retry: false,
  });

  const adDetails = tourWantAd || wantAdDetailsQuery.data;
  // The numeric id of the ad on screen. Never the URL slug: the API parseInt()s
  // the id, so "4-bed-2-bath-home" would act on want ad 4. Before the ad loads
  // the fallback is the URL param (numeric only), not localStorage, which can
  // still hold the previously viewed ad's id on the first render.
  const wantAdId = resolveWantAdId(adDetails?.wantAdId, params.wantAdId);
  const noWantAdFound = wantAdDetailsQuery.error?.wantAdNotFound === true;
  // A hard failure keeps the skeleton up, matching the old catch block that
  // never cleared it; only an explicit "not found" reveals the modal.
  const skeltonLoad =
    (!tourWantAd && wantAdDetailsQuery.isPending) ||
    (wantAdDetailsQuery.isError && !noWantAdFound);

  const adType = _.get(adDetails, "adType", "");
  const createdUserTypeId = _.get(adDetails, "userTypeId", "");
  const isWantAdAgentCreated =
    _.get(adDetails, "isAgentCreated", "") == 1 && createdUserTypeId !== 1;
  const propertyType = convertToNumber(_.get(adDetails, "prprtyType", ""));
  const adTypeBasedOnProperty = propertyType === 6 ? LOT_LAND : adType;
  const nice2Have = _.get(adDetails, "nice2Have", []);
  const WantCreatedUserId = _.get(adDetails, "userId", false);
  const [unsyndicateModalShow, setUnsyndicateModalShow] = useState(false);
  const [deleteModalShow, setDeleteModalShow] = useState(false); //
  const WantCreatedUserName =
    _.get(adDetails, "userFirstName", "") +
    " " +
    _.get(adDetails, "userLastName", "");
  const userCompany = _.get(adDetails, "userCompany", "");
  const wantAdClientId = _.get(adDetails, "clientId", "");
  const lat = _.get(adDetails, "lat");
  const lng = _.get(adDetails, "lng");
  const mapRadius = _.get(adDetails, "mapRadius", null);
  const drawnPolygon = _.get(adDetails, "drawnPolygon", null);
  const userAvatar = _.get(adDetails, "userAvatar", "");
  const wantCreatedFirstName = _.get(adDetails, "userFirstName", "");
  const isFavourite = _.get(adDetails, "isFavourite", false);
  const propertySubTypes = _.get(adDetails, "prptySubTypes", []);
  const handleIntroDetailModal = (modalState) =>
    setViewIntroductionModal(modalState);
  const INTRO_LIST_PAGE_LIMIT = 3;
  const isLoadIntroductionList =
    (isFromHomeFeed &&
      WantCreatedUserId &&
      WantCreatedUserId === loggedInUserId) ||
    (introId ? true : false);
  const [isFavouriteStatus, setIsFavourite] = useState("");
  const [modalShow, setModalShow] = useState(false); //
  const [searchText, setSearchText] = useState(""); //
  const user = loggedInUser();
  const sessionData = getLocalStorage(USER_INFO_KEY);
  const toPublicPage = usePublicWantListingRedirect(id, Boolean(user), adDetails);
  const consumerType = adType == BUY ? "Buyer" : "Renter";
  const [archiveModalShow, setArchiveModalShow] = useState(false); //
  const [isWantAdCreated, setWantAdCreated] = useState(false);
  const pageTopRef = useRef(null);

  // Introductions received against this want-ad. Paginated by scroll, so the
  // pages accumulate — replaces the manual currPage / previousPage append.
  const introductionsQuery = useInfiniteQuery({
    queryKey: ["wantAdIntroductions", wantAdId, introId],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await getIntroductions({
        wantAdId: wantAdId,
        page: pageParam,
        limit: INTRO_LIST_PAGE_LIMIT,
        isAccept: "1",
      });
      if (!response || response.errorCode !== 0) {
        throw new Error("Failed to fetch introductions");
      }
      return {
        list: _.get(response, "result.List", []) || [],
        count: _.get(response, "result.Count", 0),
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (total, page) => total + (page.list?.length || 0),
        0,
      );
      return loaded < (lastPage.count || 0) ? allPages.length + 1 : undefined;
    },
    enabled: !!isLoadIntroductionList && !!wantAdId,
  });

  const introductionList = useMemo(
    () => introductionsQuery.data?.pages.flatMap((page) => page.list) ?? [],
    [introductionsQuery.data],
  );
  const introCount = introductionsQuery.data?.pages?.[0]?.count ?? 0;

  // Detail for the introduction the user opened from the list.
  const [selectedIntroId, setSelectedIntroId] = useState(null);

  const introDetailsQuery = useQuery({
    queryKey: ["introductionDetails", selectedIntroId],
    queryFn: async () => {
      const response = await getIntroductionDetaisById(selectedIntroId);
      const responseResult = _.get(response, "result", null);
      if (response.errorCode !== 0 || !responseResult) {
        throw new Error("Failed to fetch introduction details");
      }
      return responseResult;
    },
    enabled: !!selectedIntroId,
    // The modal must reflect the current state of the introduction, so this
    // opts out of the global 5-min stale window.
    staleTime: 0,
  });

  const introDetails = introDetailsQuery.data ?? [];

  // Agent's client list, re-read as the agent types in the search box.
  const agentClientsQuery = useQuery({
    queryKey: ["wantAdAgentClients", sessionData?.userInfo?.userId, searchText],
    queryFn: async () => {
      const response = await getAgentsList({
        search: searchText,
        sortBy: "createdOn",
        sortDir: "desc",
      });
      const responseResult = _.get(response, "result", null);
      if (response.errorCode !== 0 || !responseResult) {
        throw new Error("Failed to fetch agent clients");
      }
      return responseResult;
    },
    enabled: !!sessionData?.userInfo?.userId,
  });

  const results = agentClientsQuery.data ?? [];

  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );

  const [showAgentProfileViewModal, setAgentProfileViewModal] = useState(false);
  const agentProfileViewModalClose = () => setAgentProfileViewModal(false);
  const purchaseType = convertToNumber(_.get(adDetails, "prchTypeId", ""));
  const agentSlug = _.get(adDetails, "userSlug", "");

  const fireDisplaced = _.get(adDetails, "fireDisplaced", "");
  const zipcodes = _.get(adDetails, "zipCode", []);
  const isSyndicatedToPublic = _.get(adDetails, "syndicateToThePublic", false);

  //To fetch current subscription plan of user who has created wantad
  const createdUserPlanQuery = useQuery({
    queryKey: ["wantAdCreatedUserPlan", wantAdId],
    queryFn: async () => {
      const response = await getUserPlanDetails({ wantAdId: wantAdId });
      const responseResult = _.get(response, "result", null);
      if (response.errorCode !== 0 || !responseResult) {
        throw new Error("Failed to fetch Want-Listing creator plan");
      }
      return responseResult[0]?.PlanName ?? "";
    },
    enabled: !!wantAdId && !!loggedInUserId,
  });

  const wantAdCreatedUserPlan = createdUserPlanQuery.data ?? "";

  //HANDLE ZIPCODE POLYGON
  // Keyed on the zip codes themselves, so a detail refetch that returns the
  // same zips reuses the cached boundaries instead of re-requesting them.
  const boundaryZipCodes = useMemo(
    () => (zipcodes || []).map((item) => item?.zipCode).filter(Boolean),
    [zipcodes],
  );

  const boundaryQuery = useQuery({
    queryKey: ["wantAdBoundaryCoordinates", boundaryZipCodes],
    queryFn: async () => {
      const response = await getBoundaryCoordinates({
        zipCode: boundaryZipCodes,
      });
      const responseResult = _.get(response, "result", null);
      if (response.errorCode !== 0 || !responseResult) {
        throw new Error("Failed to fetch boundary coordinates");
      }
      return responseResult.map((result) => [result?.coordinates]);
    },
    enabled: boundaryZipCodes.length > 0,
  });

  const polygonCoordinates = boundaryQuery.data ?? [];

  const isMyWantAd = user?.userInfo?.userId === adDetails?.userId;
  const { data: postedOffer } = useGitchaProOffer(Boolean(isFromConfirmationPage && isMyWantAd));

  useRecordWantAdView(adDetails?.wantAdId, {
    skip: isMyWantAd,
    entry: buildViewEntry({
      landedHere: location.key === "default",
      pathname: location.pathname,
    }),
  });
  useReadingTime(adDetails?.wantAdId, { skip: isMyWantAd });

  const { flags, tenant } = useAppConfig();

  const {
    mlsName: blsMlsName,
    blsReference,
    postedLabel: blsPostedLabel,
    hasDetailMetaInfo,
  } = getWantAdDetailMeta(adDetails, tenant);

  const tabInitial = searchParams.get("tabKey");
  const [tabKey, setTabKey] = useState(tabInitial || "Want-Listing Details");

  const { sseData } = useSelector((state) => state.sseData);
  // Ignore whatever SSE event is already in Redux at mount (a previous
  // 'matchingComplete' lingers there). Only events that arrive afterwards are
  // fresh — otherwise a stale event would instantly clear the in-progress state.
  const seenSseRef = useRef(sseData);

  const [sortType, setSortType] = useState(options[0].value);

  // Social share links for this want-ad. Read-on-load rather than a mutation:
  // nothing here is user-triggered, the values only feed the share UI.
  // Keyed off the fetched want-ad rather than WANT_AD_KEY: the effect below
  // parks the slug in that key until the detail read replaces it, and a slug
  // reaches the share endpoint as a non-existent Want_ad_Id.
  const shareLinksQuery = useQuery({
    queryKey: ["wantAdShareLinks", adDetails?.wantAdId],
    queryFn: async () => {
      const response = await shareWantAdSocial({
        wantAdId: adDetails?.wantAdId,
        platform: "facebook",
      });
      return {
        shareUrl: response?.result?.shareUrl || "",
        fbUrl: response?.result?.facebookShareUrl || "",
        imageUrl: response?.result?.imageUrl || "",
      };
    },
    enabled: !!adDetails?.wantAdId && !!loggedInUserId,
  });

  const shareUrl = resolveShareUrl({
    apiShareUrl: shareLinksQuery.data?.shareUrl,
    loggedInUserId,
    href: window.location.href,
  });
  const fbUrl = shareLinksQuery.data?.fbUrl ?? "";
  const imageUrl = shareLinksQuery.data?.imageUrl ?? "";
  const shareLinkStatus =
    !shareLinksQuery.isFetching &&
    (shareLinksQuery.isError || (shareLinksQuery.isSuccess && !shareUrl))
      ? "error"
      : "loading";
  const retryShareLinks = () => shareLinksQuery.refetch();

  const tutorialPage = 5; //Want add tutorial
  let intoLinkDisplayStatus = checkTutorialDisplayStatus(tutorialPage);
  const [showIntroLink, setIntroLink] = useState(intoLinkDisplayStatus);
  const [showIntroTutorialModal, setIntroTutorialModal] = useState(false);
  const IntroTutorialModalClose = () => setIntroTutorialModal(false);
  const [displayTutorial, setDisplayTutorial] = useState(false);
  const [showSyndicateModal, setShowSyndicateModal] = useState(false);

  const { startLoading, stopLoading } = useLoading()

  const {
    mlsMatches,
    mlsSuggestions,
    totalMlsMatches,
    hasNextMlsPage,
    fetchNextMlsPage,
    isMatchesLoading,
    isFetchingNextMlsPage,
    aiRankPending,
  } = useWantAdMlsMatches({
    wantAdId: adDetails?.wantAdId,
    sortType,
    enabled: isMyWantAd && !!adDetails?.wantAdId,
  });

  const {
    isMatchingInProgress,
    isAwaitingResult: isAwaitingMatchResult,
    markComplete: markMatchingComplete,
  } = useMatchingProgress({
    serverStatus: adDetails?.matchStatus,
    hasMatches: totalMlsMatches > 0,
    onFinished: () => {
      toast.success("Your matches are ready");
      if (adDetails?.wantAdId) {
        queryClient.invalidateQueries({
          queryKey: ["wantAdMlsMatches", adDetails.wantAdId],
        });
      }
    },
  });

  useEffect(() => {
    setPollMatchStatus(isAwaitingMatchResult);
  }, [isAwaitingMatchResult]);

  // AI ranking (step 19). Inert until the tenant's display switch is on:
  // aiRank.enabled false keeps score order and today's popover exactly.
  const [showAiSuggestions, setShowAiSuggestions] = useState(false);
  const aiRank = useAiRanking(
    adDetails?.wantAdId,
    isMyWantAd && !!adDetails?.wantAdId,
  );
  // AI order only replaces the DEFAULT sort; an explicit user sort wins.
  const orderedMlsMatches = useMemo(() => {
    if (!aiRank.enabled || sortType !== "most_relevant" || !mlsMatches?.length)
      return mlsMatches;
    // Best fits: highest AI score first; rankPosition breaks ties, unranked
    // rows sort last. Mirrors the backend strict ordering.
    const keyOf = (m) => {
      const entry = aiRank.byKey.get(aiKeyForListing(m));
      return entry
        ? [-entry.aiScore, entry.rankPosition]
        : [Infinity, Number.MAX_SAFE_INTEGER];
    };
    return [...mlsMatches].sort((a, b) => {
      const ka = keyOf(a);
      const kb = keyOf(b);
      return ka[0] - kb[0] || ka[1] - kb[1];
    });
  }, [mlsMatches, aiRank, sortType]);

  //Tutorial documentation section
  //Defining tutorial steps
  const acceptTutorial = () => {
    setIntroTutorialModal(false);
    setDisplayTutorial(true);
  };

  //Tutorial status call back
  const handleJoyrideCallback = async (data) => {
    const { action, index, status, type, lifecycle } = data;
    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status) &&
      lifecycle == "complete"
    ) {
      setDisplayTutorial(false);
      setIntroLink(false);
      saveTutorialStatusToLocal(tutorialPage); //Save status
    }
  };

  const [isVertical, setIsVertical] = useState(false);
  //check if video is vertical
  useLayoutEffect(() => {
    const videoElement = document.getElementById("videoPlayer");
    if (videoElement) {
      const { videoWidth, videoHeight } = videoElement;
      setIsVertical(videoHeight > videoWidth);
    }
  });

  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);
  useEffect(() => {
    if (id) {
      setLocalStorage(WANT_AD_KEY, { wantAdId: id });
    }
  }, [id]);

  const { hasPaidPlan: isPremiumUser } = usePlan();

  useEffect(() => {
    window.scrollTo(0, 0); // land at the very top of the page
    const showSuccess = _.get(wantAd, "showSuccess", null);
    if (showSuccess && !isFromHomeFeed && !isFromAllIntroduction) {
      setSuccessModal(true);
    } else if (
      !isFromHomeFeed &&
      !isFromAllIntroduction &&
      !introId &&
      WantCreatedUserId === loggedInUserId &&
      // A non-syndicated BLS listing has no public page yet (the API
      // refuses it) - don't auto-open a share modal for a dead link.
      (!flags?.mlsAgentEditor || tenant?.id === "default" || isSyndicatedToPublic)
    ) {
      setShareModal(true);
      setWantAdCreated(true);
    }
  }, [
    introId,
    wantAdId,
    isFromHomeFeed,
    isFromAllIntroduction,
    WantCreatedUserId,
    loggedInUserId,
  ]);

  useEffect(() => {
    setIsFavourite(isFavourite);
  }, [adDetails]);

  // Live finish: SSE 'matchingComplete' for this want-ad flips the tab and
  // refetches the count without a page refresh.
  useEffect(() => {
    if (sseData === seenSseRef.current) return; // stale / already handled
    seenSseRef.current = sseData;
    const payload = sseData?.payload;
    if (
      payload?.messageType === "matchingComplete" &&
      adDetails?.wantAdId &&
      Number(payload?.subjectId) === Number(adDetails.wantAdId)
    ) {
      // onFinished (useMatchingProgress) refreshes the matches query.
      markMatchingComplete();
    }
  }, [sseData, adDetails?.wantAdId, markMatchingComplete]);

  //Syndicate want ad
  const shareAccessModalRef = useRef(null);

  const syndicateWantAdMutation = useMutation({
    mutationFn: (wantAdId) => syndicateWantAdListing(wantAdId),
    onMutate: () => {
      setShowSyndicateModal(false);
      startLoading("syndication-want-ad");
    },
    onSuccess: (syndicatedWantAd) => {
      if (syndicatedWantAd.errorCode === 0 && syndicatedWantAd.result) {
        if (!shareAccessModalRef.current) setSyndicateSuccessModal(true);
        queryClient.invalidateQueries({ queryKey: ["wantAdDetails"] });
        // Now public on Gitcha, so the share link has to be re-resolved.
        queryClient.resetQueries({ queryKey: ["wantAdShareLinks"] });
        queryClient.invalidateQueries({ queryKey: ["wantAdCreatedUserPlan"] });
        queryClient.invalidateQueries({ queryKey: ["onboardingProgress"] });
      }
    },
    onError: () => {
      toast.error(
        "Something went wrong while syndicating want listing. Please try again later.",
        { autoClose: 5000 },
      );
    },
    onSettled: () => stopLoading("syndication-want-ad"),
  });

  const handleSyndicateWantAd = (wantAdId) => {
    syndicateWantAdMutation.mutate(wantAdId);
  };

  const unsyndicateWantAdMutation = useMutation({
    mutationFn: (wantAdId) => unsyndicateWantAdListing(wantAdId),
    onMutate: () => startLoading("syndication-want-ad"),
    onSuccess: (response) => {
      if (response.errorCode === 0 && response.result) {
        toast.success("Removed from Gitcha.com. It stays live for members.");
        queryClient.invalidateQueries({ queryKey: ["wantAdDetails"] });
        queryClient.resetQueries({ queryKey: ["wantAdShareLinks"] });
      }
    },
    onError: () => {
      toast.error(
        "Something went wrong while removing this want listing from Gitcha.com. Please try again later.",
        { autoClose: 5000 },
      );
    },
    onSettled: () => stopLoading("syndication-want-ad"),
  });

  const updateIntroPageOnScroll = () => {
    if (
      introductionsQuery.hasNextPage &&
      !introductionsQuery.isFetchingNextPage
    ) {
      introductionsQuery.fetchNextPage();
    }
  };

  // To show the share wantad popup
  const handleOnShare = async () => {

    // Signed-out visitors skip the copy-link shortcut and open the share
    // modal (Copy Link / Email).
    if (shouldCopyShareLinkDirectly({ loggedInUserId, shouldRestrictAgent })) {
      // A non-syndicated BLS listing's page opens only for its owner,
      // same-MLS agents and admins - clarify that for whoever just copied
      // the link (only reachable here by a same-MLS agent; the owner shares
      // through the audience menu on BLS tenants).
      const copiedToastMessage =
        isBlsTenant && !isSyndicatedToPublic
          ? "Link copied. Only members of your network can open it."
          : "Link copied to clipboard";
      if (shareUrl) {
        copyToClipboard(shareUrl);
        toast.success(copiedToastMessage);
        return;
      }
      // Still resolving after a save - wait for the current link rather than
      // copying nothing, or the one from before the edit.
      startLoading(SHARE_LINK_LOADING_KEY);
      try {
        // The API regenerates this want-ad's share images on the first call
        // after a save, so join the request already running instead of
        // cancelling it and paying for a second round.
        const { data } = await shareLinksQuery.refetch({ cancelRefetch: false });
        if (data?.shareUrl) {
          copyToClipboard(data.shareUrl);
          toast.success(copiedToastMessage);
        } else {
          toast.error("Couldn't generate the share link. Please try again.");
        }
      } finally {
        stopLoading(SHARE_LINK_LOADING_KEY);
      }
      return;
    }

    setWantAdCreated(false);
    setShareModal(true);
  };

  const showShareModalPopup = () => {
    setShareModal(true);
    setSuccessModal(false);
    updateLocalStorage();
  };

  /**
   *  Get introduction details by passsing introduction id
   */
  const getIntroductionDetails = (introId, IsAccept = 0) => {
    if (IsAccept == 1 || IsAccept == true) {
      setLocalStorage(INVENTORY_KEY, {
        introductionId: introId,
        adType: adType,
      });
      navigate(pageURLs.viewIntroducedInventory, {
        state: { from: location.pathname + location.search },
      });
    } else {
      handleIntroDetailModal(true);
      setSelectedIntroId(introId || null);
    }
  };

  const handleSuccessModalClose = () => {
    setSuccessModal(false);
    updateLocalStorage();
  };

  const handleReportPopup = (isShow) => setReportModal(isShow);

  const updateLocalStorage = () => {
    const wantAd = getLocalStorage(WANT_AD_KEY);
    const wantAdId = _.get(wantAd, "wantAdId", null);
    setLocalStorage(WANT_AD_KEY, { wantAdId: wantAdId });
  };

  const loadEachNiceToHave = (section) => {
    const selectedItems =
      nice2Have && nice2Have.filter((item) => item.tab === section);
    return loadNiceToHaveList(selectedItems, section.charAt(0));
  };

  const finalNiceToHaveList = () => {
    const niceToHaveMasterArray = adTypeBasedOnProperty === LOT_LAND ?
      NICE_2_HAVES_LOT_LAND_CATEGORIES.map((item) => item.category) :
      NICE_2_HAVES_CATEGORIES_MASTER_LIST.map((item) => item.category);

    const finalList =
      niceToHaveMasterArray &&
      niceToHaveMasterArray.map((nh) => {
        return loadEachNiceToHave(nh);
      });
    return finalList;
  };
  /**
   * Navigate to edit wantad on selecting edit option from dropdown menu
   */
  const onEdit = () => {
    if (userType == 2) {
      navigate(`${pageURLs.editWantAdForAgents}/${adDetails?.wantAdId}`, { state: { data: adDetails } });
    } else {
      navigate(pageURLs.agentEditwantad, { state: { data: adDetails } });
    }
  };

  const [closeOutShow, setCloseOutShow] = useState(false);
  const deleteCheckRef = useRef(false);
  const onWantAdDelete = async () => {
    if (deleteCheckRef.current) return;
    deleteCheckRef.current = true;
    try {
      // The API's close-out status is the source of truth (closed/terminated are final).
      const status = await getCloseOutContext(wantAdId)
        .then((context) => context?.status)
        .catch(() => null);
      if (["ACTIVE", "PENDING", "ARCHIVED"].includes(status)) {
        setCloseOutShow(true);
      } else {
        setDeleteModalShow(true);
      }
    } finally {
      deleteCheckRef.current = false;
    }
  };

  /**
   * To handle the want-ad delete
   */
  const deleteWantAdMutation = useMutation({
    mutationFn: (id) => deleteWantAd(id),
    onSuccess: (response) => {
      const responseResult = _.get(response, "result", null);
      if (response.errorCode === 0 && responseResult) {
        toast.success("Your Want-Listing has been deleted.");
        setDeleteModalShow(false);
        queryClient.invalidateQueries({ queryKey: ["wantAdDetails"] });
        navigate(pageURLs.agentMyWantAdListings);
      } else {
        console.log("Something went wrong");
      }
    },
    onError: (err) => {
      console.log("Error", err);
    },
  });

  // Delete and pause act only on the ad that is loaded, never on a stored id.
  const handleDeleteWantAd = () => {
    const loadedWantAdId = toWantAdId(adDetails?.wantAdId);
    if (loadedWantAdId) {
      deleteWantAdMutation.mutate(loadedWantAdId);
    }
  };
  /**
   * Show popup to handle pause want ad
   * @param {*} id
   */
  const onWantAdArchived = (id) => {
    setArchiveModalShow(true);
  };

  /**
   * To handle the want-ad  pause
   */
  const archiveWantAdMutation = useMutation({
    mutationFn: (id) => archiveWantAd(id),
    onSuccess: (response) => {
      const responseResult = _.get(response, "result", null);
      if (response.errorCode === 0 && responseResult) {
        toast.dismiss();
        toast.success("Your Want-Listing has been paused.");
        setArchiveModalShow(false);
        queryClient.invalidateQueries({ queryKey: ["wantAdDetails"] });
        queryClient.invalidateQueries({ queryKey: ["wantAdCreatedUserPlan"] });
      } else {
        console.log("Something went wrong");
      }
    },
    onError: (err) => {
      console.log("Error", err);
    },
  });

  const handleArchiveWantAd = () => {
    const loadedWantAdId = toWantAdId(adDetails?.wantAdId);
    if (loadedWantAdId) {
      archiveWantAdMutation.mutate(loadedWantAdId);
    }
  };

  /**
   * To handle the want-ad un pause
   */
  const unArchiveWantAdMutation = useMutation({
    mutationFn: (id) => unArchiveWantAd(id),
    onSuccess: (response) => {
      if (response.errorCode == 0) {
        toast.dismiss();
        toast.success("Your paused Want-Listing has been made public again.");
        queryClient.invalidateQueries({ queryKey: ["wantAdDetails"] });
        queryClient.invalidateQueries({ queryKey: ["wantAdCreatedUserPlan"] });
      } else {
        console.log("Something went wrong");
      }
    },
    onError: (err) => {
      console.log("Error", err);
    },
  });

  const handleUnArchiveWantAd = (wantAd) => {
    // GTCH-853: this market posts through the BLS. Explain rather than
    // letting the request come back 403.
    if (isMarketRestrictedWantAd(adDetails) || isMarketRestrictedWantAd(wantAd)) {
      setMarketRestrictedReason("POLICY_LOCKED");
      return;
    }
    const unpauseWantAdId = toWantAdId(wantAd?.wantAdId);
    if (unpauseWantAdId) {
      if (
        (wantAd?.Buy_or_Rent == 2 && wantAd?.Move_in_TimeLine == 11) ||
        wantAd?.Buy_or_Rent == 3
      ) {
        if (userType == 2) {
          navigate(`${pageURLs.editWantAdForAgents}/${adDetails?.wantAdId}`, {
            state: { data: adDetails, isFromUnPause: true },
          });
        } else {
          navigate(pageURLs.agentEditwantad, {
            state: { data: adDetails, isFromUnPause: true },
          });
        }
      } else {
        unArchiveWantAdMutation.mutate(unpauseWantAdId);
      }
    }
  };

  /**
   * To load nice to have list view
   * @param {*} list
   * @param {*} uniqueType
   * @returns
   */
  const loadNiceToHaveList = (list, uniqueType) => {
    return (
      list &&
      list.map((tl) => {
        const id = _.get(tl, "id");
        const itemText = _.get(tl, "text", "");
        const icon = _.get(tl, "icon", "");
        return (
          <div className="nicetohaves-tag" key={id + uniqueType}>
            <div className="nicetohaves-tag-icon">
              <img
                src={
                  icon
                    ? (() => {
                      try {
                        return require("../../images/" + icon);
                      } catch (e) {
                        return PlaceHolderIcon;
                      }
                    })()
                    : PlaceHolderIcon
                }
                style={{ width: 16, height: 16 }}
              />
            </div>
            <span className="wanted-details-itemtext">{itemText}</span>
          </div>
        );
      })
    );
  };

  /**
   * To handle display adTitle
   * @returns
   */
  const loadAdTitle = () => {
    let adTypeTile = "Buy";
    if (adType === LONG_RENT) {
      adTypeTile = "Rent";
    } else if (adType === SHORT_RENT) {
      adTypeTile = "Stay";
    }
    return (
      <>
        {" "}
        <span className="want-ad-tag align-middle green-tag-bg mb-0 f-size-10">
          {adTypeTile}
        </span>
      </>
    );
  };

  /**
   * To handle strengthen want ad details display
   * @returns
   */
  const loadStrengthenDetails = () => {
    if (_.get(adDetails, "isStrengthen", "")) {
      if (adType === BUY) {
        return (
          <>
            {" "}
            {_.get(adDetails, "prchTypeTxt", "") && (
              <li>
                <span className="list-icn-wrap">
                  <img src={PrResIcon} />
                </span>
                {_.get(adDetails, "prchTypeTxt", "")}
                {(_.get(adDetails, "is1031Exchange", "") == "Yes" ||
                  _.get(adDetails, "is1031Exchange", "") == 1) &&
                  " - 1031 exchange"}
              </li>
            )}
            {_.get(adDetails, "finTypeTxt", "") && (
              <li>
                <span className="list-icn-wrap">
                  <img src={FinancingIcon} />
                </span>
                Financing: {_.get(adDetails, "finTypeTxt", "")}
                {(_.get(adDetails, "isPreApprLoan", "") == "Yes" ||
                  _.get(adDetails, "isPreApprLoan", "") == 1) &&
                  "  - pre approved"}
              </li>
            )}
          </>
        );
      } else if (adType === LONG_RENT) {
        return (
          <>
            {" "}
            {_.get(adDetails, "creditScoreText", "") && (
              <li>
                <span className="list-icn-wrap">
                  <img src={FinancingIcon} />
                </span>
                Credit Score : {_.get(adDetails, "creditScoreText", "")}
              </li>
            )}
          </>
        );
      }
    }
  };
  /**
   * To display timeline in details
   * @returns
   */
  const loadTimeLine = () => {
    return loadTimeLineHtml(adType, adTypeBasedOnProperty, adDetails);
  };

  const loadBuyerAgreement = () => {
    return loadBuyerAgreementHtml(adDetails);
  };
  /**
   * To display preferred location
   * @returns
   */
  const loadPreferredLocation = () => {
    return loadPreferredLocationHtml(adDetails);
  };
  /**
   * To display musthaves section
   * @returns
   */
  const loadAdMustHaves = () => {
    return buildWantAdMustHavesHTML({
      adType: adType,
      adTypeBasedOnProperty: adTypeBasedOnProperty,
      formValues: adDetails,
    });
  };

  /**
   * To handle the want-ad favorite/unfavorite
   */
  const handleFavoriteOrUnfavoriteWantAd = async (
    IsFavourite,
    Want_ad_Id,
    clientId = [],
  ) => {
    if (Want_ad_Id) {
      let data = {
        clientId: IsFavourite == false ? clientId : null,
        wantAdId: Want_ad_Id,
      };
      await (
        IsFavourite == false ? favouriteWantAd(data) : unFavouriteWantAd(data)
      )
        .then((response) => {
          if (response.errorCode == 0) {
            let message = "";
            if (clientId.length > 0) {
              message =
                "Want-Listing has been favorited for " +
                clientId.length +
                (clientId.length > 1 ? " clients" : " client");
            } else {
              message =
                IsFavourite == true
                  ? "Want-Listing has been removed from your favorites"
                  : "Want-Listing has been added to your favorites";
            }

            toast.success(message);
            setModalShow(false);
            setIsFavourite(!IsFavourite);
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  /**
   * To handle textbox value change on agent client search
   * @param {any} event
   */
  const onChangeText = (event) => {
    let value = "";
    if (event) {
      value = handleTextBoxChange(event);
    }

    setSearchText(value);
  };

  /**
   * to handle clients favorites action
   * @param {*} agentIds
   */
  const handleClientFavoriteWantAd = (agentIds) => {
    handleFavoriteOrUnfavoriteWantAd(isFavourite, wantAdId, agentIds);
  };

  /**
   * handle agents favorite action, to open client list popup
   */
  const handleAgentsFavoriteAction = () => {
    setModalShow(true);
  };

  const isNiceToHaveExist = nice2Have.length > 0;
  const renderTooltip = (props) => (
    <Tooltip
      id="button-tooltip"
      placement="bottom-start"
      className="custom-tooltip"
      {...props}
    >
      {propertyType === IS_LAND || propertyType === IS_MULTI_FAMILY
        ? getTooltipText(propertyType)
        : propertySubTypes &&
        propertySubTypes.length > 0 &&
        getCombinedText(propertySubTypes)}
    </Tooltip>
  );

  const handleSortParam = (selectedOption) => {
    setSortType(selectedOption.value);
  };

  const renderAuthorProfile = () => {
    const currentUserIsAgent = userType === USER_TYPE_AGENT;
    const isAgentCreated = _.get(adDetails, "isAgentCreated", "") == 1;

    const agentInfoPath = `/agent-info/${agentSlug}`;
    const agentNavState = { state: { canGoBack: true } };

    const bioLinkLabel = isGitchaDefaultWebsite ? "View bio & contact info" : "Message";

    const linkAction = authorLinkAction({
      isGitchaDefaultWebsite,
      isMyWantAd,
      isLoggedIn: !!loggedInUserId,
      authorUserId: _.get(adDetails, "userId", null),
    });

    const agentNavLink = linkAction !== "bio" ? (
      <AuthorMessageLink
        action={linkAction}
        authorUserId={adDetails.userId}
        label={bioLinkLabel}
        onLogin={() => dispatch(openModal({ name: "loginModal" }))}
      />
    ) : opensGitchaBio ? (
      <a href={bioHref(agentSlug)} target="_blank" rel="noopener noreferrer" className="blue-text">
        {bioLinkLabel}
      </a>
    ) : (
      <NavLink
        to={agentInfoPath}
        state={{ canGoBack: true }}
        className="blue-text"
      >
        {bioLinkLabel}
      </NavLink>
    );

    const consumerFallback = currentUserIsAgent ? (
      <>
        <span className="list-icn-wrap">
          <img
            src={ConsumerIcon}
            alt="avatar"
            className="property-agent-profile"
            width="25"
            height="25"
          />
        </span>
        <div className="property-not-agent-name mx-0">
          <span className="timeline-value">{consumerType}</span>
        </div>
      </>
    ) : (
      <>
        <span className="list-icn-wrap">
          <img src={ConsumerIcon} />
        </span>
        {consumerType}{" "}
        {wantAdCreatedUserPlan === INVESTOR_MEMBER_PLAN ? (
          <span className="pro-gold-badge">Investor Member</span>
        ) : (
          ""
        )}
      </>
    );

    return (
      <AgentProfileCard
        isAgentCreated={isAgentCreated && createdUserTypeId !== 1}
        isAgentPremium={isPremiumAgentPlan(wantAdCreatedUserPlan)}
        userAvatar={userAvatar}
        userFirstName={_.get(adDetails, "userFirstName", "")}
        userLastName={_.get(adDetails, "userLastName", "")}
        userCompany={userCompany}
        avatarSize="40"
        avatarWrapperClassName="list-icn-wrap"
        onAvatarClick={() => openBio(agentSlug, agentNavState.state)}
        linkComponent={agentNavLink}
        fallback={consumerFallback}
      />
    );
  };

  // A plain render function, not a component: a `const X = () => {...}`
  // defined inside this component's body is a fresh component type on every
  // render, which remounts its whole subtree - the same shape that produced
  // the earlier "Rendered more hooks than during the previous render" crash
  // in this feed once a hook got added inside it.
  const renderMatchesTab = () => {
    const mlsHasData = mlsMatches?.length > 0;

    if (isMatchesLoading) {
      return <MatchesSkeleton />;
    }

    const handleShowMoreClick = () => fetchNextMlsPage();

    // Suggestions are a distinct section from matches (see the divider
    // below) and must render even with zero matches - that's the case they
    // exist to soften. Only the matches list/sort/show-more are gated on
    // mlsHasData; the outer shell and suggestions render regardless.
    return (
      <>
        {mlsHasData && (
          <>
            <div className="sort-items-panel">
              <div className="sort-items-inner-wrapper">
                <div className="row">
                  <h1 className="">
                    Active Listings That Match "{adDetails?.adTitle}"
                  </h1>
                  <div className="d-flex mb-4 align-items-center">
                    <div className="col">
                      {aiRank.enabled && mlsSuggestions?.length > 0 && (
                        <p className="ai-match-split-note">
                          <span
                            className="ai-match-split-note__spark"
                            aria-hidden="true"
                          >
                            ✦
                          </span>
                          These homes meet every one of your buyer&apos;s
                          must-haves.
                          <OverlayTrigger
                            placement="bottom"
                            overlay={
                              <Tooltip id="ai-match-split-tip">
                                More suggestions: AI picks that miss one
                                must-have, usually budget. Their percentage
                                shows how well everything else fits.
                              </Tooltip>
                            }
                          >
                            <button
                              type="button"
                              className="ai-match-split-note__why"
                            >
                              What about the rest?
                            </button>
                          </OverlayTrigger>
                        </p>
                      )}
                    </div>
                    <div style={{ width: "auto !important" }}>
                      <div className="d-flex select-wrap position-relative gap-3">
                        <Form.Label style={{ fontSize: 15 }}>Sort By:</Form.Label>
                        <SelectDropDown
                          id="multi3"
                          name="colors3"
                          options={options}
                          className="basic-single sort-select d-inline-block mb-0"
                          classNamePrefix="select"
                          placeholder="Select"
                          value={options.find((opt) => opt.value === sortType)}
                          onChange={handleSortParam}
                          styles={{
                            dropdownIndicator: (provided, state) => ({
                              ...provided,
                              transform:
                                state.selectProps.menuIsOpen && "rotate(180deg)",
                            }),
                            menu: (provided) => ({
                              ...provided,
                              width: "max-content",
                              minWidth: "100%",
                              left: "auto",
                              right: 0,
                            }),
                            option: (provided) => ({
                              ...provided,
                              whiteSpace: "nowrap",
                            }),
                          }}
                          errorClass="d-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </>
        )}

        {!mlsHasData &&
          (aiRankPending ? (
            // Kept deliberately: a fresh want-ad's matches ARE the AI's
            // answer and take ~30s, so this is the one place a pending state
            // is honest. Wording matches the want-ad table's pill.
            <div className="ai-rank-pending" role="status" aria-live="polite">
              <span className="ai-rank-pending__spark" aria-hidden="true">
                ✦
              </span>
              Scoring listings for this buyer. This list fills in
              automatically.
            </div>
          ) : (
            <div className="no-result-wrap">
              <h3>No matches found</h3>
              <p>There are no matching MLS listings for this want-listing yet.</p>
            </div>
          ))}

        {/* Suggestions are demoted matches, so they only make sense next to
            the matches they were demoted from. On their own they contradicted
            the "No matches found" state directly above them. */}
        {mlsHasData && (
          <div className="matches-inner-tab-body">
            {mlsHasData && (
              <>
                {orderedMlsMatches.map((m, index) => (
                  <MlsMatchCard
                    list={m}
                    ai={
                      aiRank.enabled
                        ? aiRank.byKey.get(aiKeyForListing(m)) || null
                        : null
                    }
                    key={`mls-${m?.mlsListingKey || m?.ListingKey || index}`}
                  />
                ))}
                {hasNextMlsPage ? (
                  <div className="text-center pt-3 pb-3 w-100">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleShowMoreClick()}
                      disabled={isFetchingNextMlsPage}
                    >
                      {isFetchingNextMlsPage ? "Loading..." : "Show More"}
                    </button>
                  </div>
                ) : null}
              </>
            )}

            {mlsSuggestions?.length > 0 && !showAiSuggestions && (
              <div className="text-center pt-3 pb-3 w-100">
                <button
                  className="ai-suggestions-toggle"
                  onClick={() => setShowAiSuggestions(true)}
                >
                  More suggestions ({mlsSuggestions.length})
                </button>
              </div>
            )}

            {mlsSuggestions?.length > 0 && showAiSuggestions && (
              <>
                {/* Deliberately below the matches and labelled as excluded:
                    the agent should never read this section as part of
                    their match count. */}
                <div className="mls-suggestions__divider">
                  <span className="line" />
                  <span className="label">More suggestions</span>
                  <span className="line" />
                </div>
                {mlsSuggestions.map((m, index) => (
                  <MlsMatchCard
                    isSuggestion
                    list={m}
                    ai={
                      aiRank.enabled
                        ? aiRank.byKey.get(aiKeyForListing(m)) || null
                        : null
                    }
                    key={`sugg-${m?.mlsListingKey || m?.ListingKey || index}`}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </>
    );
  };

  const { shouldRestrictAgent } = useAgentGitchaRestrictions();
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const trackMatchesStep = isMyWantAd && !isGitchaDefaultWebsite && userType === USER_TYPE_AGENT;
  const ownerOnboarding = useOnboardingProgress(trackMatchesStep, QUIET_PROGRESS_OPTIONS).data;
  useMarkMatchesSeen(trackMatchesStep && tabKey === "Matches", ownerOnboarding);
  const [showMatchesFeatureModal, setShowMatchesFeatureModal] = useState(false);

  // On MLS tenants an unsyndicated want-listing is not public yet, so there is
  // nothing to share - that case gets the "Syndicate to Gitcha" panel instead.
  // Gitcha has no syndicate panel, so gating there could strand a listing with
  // neither. Wherever the GitchaSyndication card renders (mlsAgentEditor
  // tenants, not gitcha.com) it already owns sharing, so this panel is hidden
  // there to avoid duplicating it.
  const shouldDisplayShareCard =
    sessionData?.userInfo?.userId &&
    isMyWantAd &&
    (!flags?.mlsAgentEditor || isSyndicatedToPublic) &&
    (!flags?.mlsAgentEditor || isGitchaDefaultWebsite);

  // BLS tenants refuse a public share page for a non-syndicated listing, so
  // the owner shares through the audience menu instead of a one-click copy.
  const isBlsTenant = flags?.mlsAgentEditor && !isGitchaDefaultWebsite;
  const [shareAccessModal, setShareAccessModal] = useState(null);

  const openShareAccess = (audience) => {
    shareAccessModalRef.current = audience;
    setShareAccessModal(audience);
    trackEvent({
      name: "gitcha_share_menu_opened",
      attributes: { wantAdId, audience, isSyndicated: !!isSyndicatedToPublic },
    });
  };

  const { redirectToBLSEnv } = useMLSAgentAtGitchaRedirect();

  useEffect(() => {
    if (shouldRestrictAgent && !isSyndicatedToPublic && adDetails?.slug) {
      redirectToBLSEnv({
        path: `${pageURLs.wantAdDetails}/${adDetails?.slug}`,
        onGitchaEnvFallback: null,
      });
    }
  }, [shouldRestrictAgent, isSyndicatedToPublic, adDetails?.slug]);

  useEffect(() => {
    if (isGitchaDefaultWebsite && tabKey === "Matches") {
      setTabKey("Want-Listing Details");
      setShowMatchesFeatureModal(true);
    }
  }, [isGitchaDefaultWebsite]);

  const dispatch = useDispatch()
  const introRequested = searchParams.get("intro") === "1";
  const introAdLoaded = Boolean(adDetails?.wantAdId);

  useEffect(() => {
    if (!introRequested || isGitchaDefaultWebsite || !introAdLoaded) return undefined;
    if (!loggedInUserId) {
      dispatch(openModal({ name: "loginModal" }));
      return undefined;
    }
    const timer = setTimeout(() => {
      const target = document.querySelector('[data-member-tour="send-intro"]');
      if (target) target.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 400);
    return () => clearTimeout(timer);
  }, [introRequested, isGitchaDefaultWebsite, introAdLoaded, loggedInUserId, dispatch]);

  return (
    <>
      <SeoHead adDetails={adDetails} />
      <span ref={pageTopRef}></span>

      {displayTutorial ? (
        <JoyRide
          steps={tutorialSteps}
          tooltipComponent={TutorialToolTip}
          continuous
          callback={handleJoyrideCallback}
          disableScrolling={true}
        />
      ) : (
        ""
      )}

      {skeltonLoad || toPublicPage ? (
        <WantadDetailsSkeleton />
      ) : (
        <>

          <PageWrapper>
            {!noWantAdFound && (
              <>
                {" "}
                <div className="main-content p-0 agent-no-login-content">
                  {(isFromHomeFeed || isFromAllIntroduction || introId) && (
                    <BackButton
                      className="mb-24 d-inline-block"
                      onClick={() => {
                        if (isFromConfirmationPage) {
                          navigate(pageURLs.agentMyWantAdListings);
                          return;
                        }
                        // Only step back within the app's own history. idx is 0
                        // when the detail page is the first entry in this tab
                        // (opened directly or from an external site), so going
                        // back would leave Gitcha - fall back to the feed instead.
                        const canGoBackInApp = window.history.state?.idx > 0;
                        navigate(canGoBackInApp ? -1 : pageURLs.home);
                      }}
                    />
                  )}
                </div>
                {isFromConfirmationPage && isMyWantAd && !postedClosed ? (
                  <PostedBanner
                    networkName={tenant?.mlsShortName || "MLS"}
                    isBls={isBlsTenant}
                    isSyndicated={!!isSyndicatedToPublic}
                    isSyndicating={syndicateWantAdMutation.isPending}
                    onShare={() => (isBlsTenant ? openShareAccess("members") : setShareModal(true))}
                    onSyndicate={() => handleSyndicateWantAd(_.get(adDetails, "wantAdId", ""))}
                    showPro={Boolean(postedOffer && !postedOffer.isPro && !postedOffer.onTrial && (postedOffer.canStartTrial || postedOffer.canUpgrade))}
                    onTryPro={() => {
                      setProReasonText(
                        "With Gitcha Pro, home shoppers who find your Buyer Want-Listings on Gitcha.com can send you their name, timing and what they need.",
                      );
                      setPremiumPlanModal(true);
                    }}
                    onClose={() => setPostedClosed(true)}
                  />
                ) : null}
                <div className={isMyWantAd ? "tab-wrap tab-wrap-fix" : ""}>
                  <Tabs
                    className="mb-3"
                    style={isMyWantAd ? {} : { display: "none" }}
                    activeKey={
                      isMyWantAd && !isMatchingInProgress
                        ? tabKey
                        : "Want-Listing Details"
                    }
                    onSelect={(k) => {
                      if (k === "Matches" && isMatchingInProgress) return;
                      if (k === "Matches") {
                        if (isGitchaDefaultWebsite) {
                          setShowMatchesFeatureModal(true);
                          return;
                        }
                        redirectToBLSEnv({ path: `${pageURLs.wantAdDetails}/${adDetails?.slug}?tabKey=Matches`, onGitchaEnvFallback: () => { } });
                      }
                      setTabKey(k);

                      // Sync URL so back-navigation lands on the right tab.
                      // When the user opens an MLS match from inside the
                      // Matches tab and clicks Back from the detail page,
                      // they should return here on the same tab — that only
                      // works if tabKey is in the URL when they leave.
                      const next = new URLSearchParams(searchParams);
                      if (k && k !== "Want-Listing Details") {
                        next.set("tabKey", k);
                      } else {
                        next.delete("tabKey");
                      }
                      // Keep router state (e.g. the listing preselected for
                      // introductions), which a bare replace would drop.
                      setSearchParams(next, {
                        replace: true,
                        state: location.state,
                      });
                    }}
                  >
                    <Tab
                      eventKey={"Want-Listing Details"}
                      title={"Want-Listing Details"}
                    >
                      <div className="row">
                        <div className="col-12 col-md-7 ps-md-3 col-lg-8 ps-lg-3 col-xl-8 ps-xl-2" style={{ width: shouldRestrictAgent && !shouldDisplayShareCard ? '100%' : null }}>
                          <div className="wantad-confirmation-wrap mb-4">
                            <div className="confirmation-top-sec d-md-flex justify-content-between">
                              <div className="long-txt-fix">
                                {/* FIRE DISPLACED */}
                                {fireDisplaced ? (
                                  <div className="dis-fires detail">
                                    <img
                                      src={DisplacedFired}
                                      alt="displaced-fired"
                                      className="displaced-fired"
                                      width={"9px"}
                                      height={"11px"}
                                    />
                                    DISPLACED BY FIRES
                                  </div>
                                ) : null}
                                <GitchaMemberBridge
                                  slug={adDetails?.slug}
                                  isSyndicated={isSyndicatedToPublic}
                                />
                                <div className="confirm-sub-title mb-1">
                                  Looking to:
                                  {loadAdTitle()}
                                </div>
                                <h2 data-member-tour="wl-title">
                                  {_.get(
                                    adDetails,
                                    "adTitle",
                                    "Cozy home for small family",
                                  )}{" "}
                                </h2>
                                <span className="trailing-txt">
                                  {locationDisplay(adDetails)}
                                </span>
                              </div>

                              {!sessionData?.userInfo?.userId ||
                                WantCreatedUserId !==
                                sessionData?.userInfo?.userId ? (
                                <div className="social-btns-wrap">
                                  <Dropdown className="mr-12">
                                    <Dropdown.Toggle
                                      variant=""
                                      id="dropdown-basic1"
                                    >
                                      <img src={MoreIcon} />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu align="end">
                                      <Dropdown.Item
                                        onClick={() => handleOnShare()}
                                      >
                                        Share
                                      </Dropdown.Item>
                                      {sessionData?.userInfo?.userId &&
                                        WantCreatedUserId !==
                                        sessionData?.userInfo?.userId && (
                                          <Dropdown.Item
                                            onClick={() =>
                                              handleReportPopup(true)
                                            }
                                          >
                                            Report
                                          </Dropdown.Item>
                                        )}
                                    </Dropdown.Menu>
                                  </Dropdown>
                                  {sessionData?.userInfo?.userId &&
                                    !shouldRestrictAgent &&
                                    <a
                                      role="button"
                                      href={void 0}
                                      className="social-content-btn"
                                    >
                                      {isFavouriteStatus ? (
                                        <img
                                          src={HearttIconFilled}
                                          onClick={() => {
                                            handleFavoriteOrUnfavoriteWantAd(
                                              true,
                                              wantAdId,
                                            );
                                          }}
                                        />
                                      ) : (
                                        <img
                                          src={HearttIcon}
                                          onClick={() => {
                                            sessionData?.userInfo?.userType ==
                                              2 && isPremiumUser
                                              ? handleAgentsFavoriteAction()
                                              : handleFavoriteOrUnfavoriteWantAd(
                                                false,
                                                wantAdId,
                                              );
                                          }}
                                        />
                                      )}
                                    </a>
                                  }

                                </div>
                              ) : (
                                <>
                                  {sessionData?.userInfo?.userId ? (
                                    <div className="social-btns-wrap">
                                      <ShareIconButton
                                        isBlsTenant={isBlsTenant}
                                        onShare={() => handleOnShare()}
                                      />
                                      {shouldRestrictAgent ? <></> : (
                                        <Dropdown>
                                          <Dropdown.Toggle
                                            variant=""
                                            id="dropdown-basic1"
                                          >
                                            <img src={MoreIcon} />
                                          </Dropdown.Toggle>
                                          <Dropdown.Menu align="end">
                                            <ShareMenuItems
                                              isBlsTenant={isBlsTenant}
                                              isSyndicated={!!isSyndicatedToPublic}
                                              onShareMembers={() => openShareAccess("members")}
                                              onShareEveryone={() => openShareAccess("everyone")}
                                              onStopSyndicating={() => setUnsyndicateModalShow(true)}
                                            />
                                            <Dropdown.Item
                                              onClick={() => onEdit()}
                                            >
                                              Edit
                                            </Dropdown.Item>
                                            {adDetails?.isArchived == 0 && (
                                              <Dropdown.Item
                                                onClick={() =>
                                                  onWantAdArchived(wantAdId)
                                                }
                                              >
                                                Pause
                                              </Dropdown.Item>
                                            )}
                                            {adDetails?.isArchived == 1 && (
                                              <Dropdown.Item
                                                onClick={() =>
                                                  handleUnArchiveWantAd(
                                                    adDetails,
                                                  )
                                                }
                                              >
                                                Unpause
                                              </Dropdown.Item>
                                            )}
                                            <Dropdown.Item
                                              onClick={() => onWantAdDelete()}
                                            >
                                              Delete
                                            </Dropdown.Item>
                                          </Dropdown.Menu>
                                        </Dropdown>
                                      )}
                                    </div>
                                  ) : (
                                    <PublicShareMenu
                                      isBlsTenant={isBlsTenant}
                                      wantAdId={wantAdId}
                                      title={_.get(adDetails, "adTitle", "Buyer Want-Listing")}
                                      url={`${window.location.origin}${withBase(`${pageURLs.wantAdDetails}/${adDetails?.slug || wantAdId}`)}`}
                                    />
                                  )}
                                </>
                              )}
                            </div>

                            <div className="confirm-map-wrap" data-member-tour="wl-location">
                              {/* COMMENTED OVERLAY FOR NOW*/}
                              <OverlayTrigger
                                placement="bottom-start"
                                delay={{ show: 250, hide: 400 }}
                                overlay={renderTooltip}
                              >
                                <div
                                  className={`property-tile w-auto ${fireDisplaced ? "firedisplaced" : ""}`}
                                  style={{ zIndex: 99 }}
                                >
                                  {propertyType === IS_LAND ||
                                    propertyType === IS_MULTI_FAMILY ? (
                                    <img
                                      src={getPropertyIcon(propertyType)}
                                      alt={getPropertyIcon(propertyType)}
                                      className="property-icon-img"
                                    />
                                  ) : (
                                    propertySubTypes &&
                                    propertySubTypes.length > 0 &&
                                    propertySubTypes.map((subtype) => {
                                      return (
                                        <img
                                          src={getPropertyIcon(subtype)}
                                          alt={getPropertyIcon(subtype)}
                                          className="property-icon-img"
                                        />
                                      );
                                    })
                                  )}
                                </div>
                              </OverlayTrigger>
                              <MapBoxComponent
                                center={[lng, lat]}
                                radiusMiles={drawnPolygon ? null : mapRadius}
                                polygonCoordinates={drawnPolygon ? [[drawnPolygon]] : polygonCoordinates}
                                showPolygon={!!drawnPolygon || zipcodes.length > 0}
                              />
                            </div>
                            <div data-member-tour="wl-must-haves">{loadAdMustHaves()}</div>

                            {isNiceToHaveExist ? (
                              <div className="nicetohaves-wrap" data-member-tour="wl-nice-to-haves">
                                <h2 className="wanted-details-head">
                                  Nice-to-haves
                                </h2>
                                <div className="nicetohaves-inner-wrap">
                                  {finalNiceToHaveList()}
                                </div>
                              </div>
                            ) : (
                              ""
                            )}

                            {_.get(adDetails, "adDesc", "") ? (
                              <div className="nicetohaves-wrap" data-member-tour="wl-description">
                                <h2 className="wanted-details-head">
                                  Description
                                </h2>
                                <p className="long-txt-fix timeline-value desc-block">
                                  {_.get(adDetails, "adDesc", "")}
                                </p>
                              </div>
                            ) : (
                              ""
                            )}

                            {_.get(adDetails, "videoUrl", "") && (
                              <div className="video-wrap">
                                <video
                                  id="videoPlayer"
                                  controls
                                  width={isVertical ? "300px" : "100%"} // Width 300px for vertical, auto for horizontal
                                  height={isVertical ? "500px" : "auto"} // Auto height for vertical, 500px for horizontal
                                  className="video-player"
                                  preload="metadata"
                                  poster={_.get(
                                    adDetails,
                                    "thumbnailImgUrl",
                                    "",
                                  )}
                                >
                                  <source
                                    src={_.get(adDetails, "videoUrl", "")}
                                    type="video/mp4"
                                  />
                                  Your browser does not support the video tag.
                                </video>
                              </div>
                            )}

                            <div
                              className={
                                (adType == BUY ||
                                  _.get(adDetails, "prprtyType", "") ==
                                  IS_LAND) &&
                                  _.get(adDetails, "isAgentCreated", "") == 1 ? `buyer-info ${getRequestedConcessions(adDetails?.otherConcessions) ? "nicetohaves-wrap" : ""}` : "buyer-info"
                              }
                              data-member-tour="wl-details"
                            >
                              <h2 className="wanted-details-head">
                                Additional details
                              </h2>
                              <ul>
                                <li>{renderAuthorProfile()}</li>
                                {loadBuyerAgreement()}
                                {loadTimeLine()}
                                {loadPreferredLocation()}
                                {loadStrengthenDetails()}
                              </ul>
                              {hasDetailMetaInfo && (
                                <div className="detail-meta-info">
                                  {blsMlsName && (
                                    <div className="meta-item">
                                      <span className="meta-label">Network</span>
                                      <span className="meta-value">
                                        {blsMlsName}
                                      </span>
                                    </div>
                                  )}
                                  {blsReference && (
                                    <div className="meta-item">
                                      <span className="meta-label">
                                        {blsMlsName ? "BLS ID" : "Listing ID"}
                                      </span>
                                      <span className="meta-value">
                                        #{blsReference}
                                      </span>
                                    </div>
                                  )}
                                  {blsPostedLabel && (
                                    <div className="meta-item">
                                      <span className="meta-label">
                                        Posted
                                      </span>
                                      <span className="meta-value">
                                        {blsPostedLabel}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {wantAdCreatedUserPlan === INVESTOR_MEMBER_PLAN &&
                              purchaseType === 3 && (
                                <div className="buyer-info">
                                  {_.get(adDetails, "investmentId", [])
                                    ?.length > 0 ||
                                    _.get(adDetails, "capRateId", null) ? (
                                    <h2>What This Investor is Open to</h2>
                                  ) : (
                                    ""
                                  )}
                                  <ul>
                                    {loadInvestmentCriteria(adDetails)}
                                    {_.get(adDetails, "capRateId", null) && (
                                      <li>
                                        <span className="list-icn-wrap">
                                          <img src={MinCapIcon} />
                                        </span>
                                        Minimum cap rate of{" "}
                                        {_.get(adDetails, "capRateId", "") == 15
                                          ? "15+"
                                          : _.get(adDetails, "capRateId", "")}
                                        %
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              )}
                          </div>
                        </div>

                        {!isFromAllIntroduction && (
                          <WantListingRail>
                            {isMyWantAd && isMatchingInProgress && (
                              <div className="matches-loading-widget mb-4">
                                <span
                                  className="matches-loading-widget__spinner"
                                  aria-hidden="true"
                                />
                                <div className="matches-loading-widget__text">
                                  <h6>Finding your matches</h6>
                                  <p>
                                    We're scanning listings for potential
                                    matches. This won't take long. They'll
                                    appear in the Matches tab once it finishes.
                                  </p>
                                </div>
                              </div>
                            )}
                            {!isMyWantAd ? (
                              <div style={{ marginBottom: 24, pointerEvents: tourWantAd ? "none" : undefined }} inert={tourWantAd ? "" : undefined} data-member-tour="message">
                                <SendMessage
                                  wantAd={adDetails}
                                  signedOutComposer={
                                    agentSlug &&
                                    isWantAdAgentCreated &&
                                    isGitchaDefaultWebsite &&
                                    _.get(adDetails, "agentContactable", false) ? (
                                      <WantContactPanel
                                        embedded
                                        agent={{
                                          slug: agentSlug,
                                          firstName: _.get(adDetails, "userFirstName", ""),
                                          lastName: _.get(adDetails, "userLastName", ""),
                                          company: _.get(adDetails, "userCompany", ""),
                                        }}
                                        wantAdId={wantAdId}
                                        title={_.get(adDetails, "adTitle", "")}
                                        network={_.get(adDetails, "network", null)}
                                      />
                                    ) : null
                                  }
                                  isAgentCreated={isWantAdAgentCreated}
                                  isAgentPremium={isPremiumAgentPlan(
                                    wantAdCreatedUserPlan,
                                  )}
                                />
                              </div>
                            ) : null}
                            {isMyWantAd && (
                              <div style={{ pointerEvents: tourWantAd ? "none" : undefined }} inert={tourWantAd ? "" : undefined} data-member-tour="share">
                              <GitchaSyndication
                                wantAdId={wantAdId}
                                isSyndicated={!!isSyndicatedToPublic}
                                adDetails={adDetails}
                                shareUrl={shareUrl}
                                fbUrl={fbUrl}
                                imageUrl={imageUrl}
                                isWantAdCreated={isWantAdCreated}
                                onShowOnGitcha={() => setShowSyndicateModal(true)}
                                posterPlan={wantAdCreatedUserPlan}
                                shareStatus={shareLinkStatus}
                                onRetryShare={retryShareLinks}
                              />
                              </div>
                            )}
                            {WantCreatedUserId &&
                              WantCreatedUserId != loggedInUserId &&
                              loggedInUserId ? (
                              <div style={{ pointerEvents: tourWantAd ? "none" : undefined }} inert={tourWantAd ? "" : undefined} data-member-tour="send-intro">
                                <SendIntroduction
                                  wantAdId={wantAdId}
                                  preselectListingKey={preselectListingKey}
                                  WantCreatedUserName={WantCreatedUserName}
                                  WantCreatedUserId={WantCreatedUserId}
                                  wantAdClientId={wantAdClientId}
                                  wantAdDetails={adDetails}
                                  tourListing={tourWantAd?.tourIntroListing}
                                />
                              </div>
                            ) : isLoadIntroductionList &&
                              introductionList?.length ? (
                              <ViewWantAdIntroductions
                                introCount={introCount}
                                list={introductionList}
                                updateIntroPageOnScroll={
                                  updateIntroPageOnScroll
                                }
                                getIntroductionDetails={getIntroductionDetails}
                              />
                            ) : (
                              <>
                                <div >

                                  {loggedInUserId && !shouldRestrictAgent && (
                                    <div className="ad-strengthen-wrap mb-0 mb-sm-4">
                                      <div className="icon-wrap">
                                        <img
                                          src={AdIntroIcon}
                                          alt={AdIntroIcon}
                                        />
                                      </div>
                                      <h6 className="send-instruction-head">
                                        When you receive a new introduction
                                        you'll see it here
                                      </h6>
                                      <p className="mb-2 wanted-info-listdetail">
                                        Start a message thread by either sending
                                        or responding to an introduction on a
                                        Want-Listing
                                      </p>
                                    </div>
                                  )}

                                  {!loggedInUserId && (
                                    <div className="ad-strengthen-wrap mb-0 mb-sm-4">
                                      <SignedOutIntroCTA />
                                    </div>
                                  )}

                                  {shouldDisplayShareCard && (
                                    <div className="share-card-wrapper share-sidecard">
                                      <ShareCard
                                        wantAdId={wantAdId}
                                        isSideCard={true}
                                        adDetails={adDetails}
                                        isWantAdCreated={isWantAdCreated}
                                        shareLink={shareUrl}
                                        fromWantAdDetails={true}
                                        imageLink={imageUrl}
                                        fbLink={fbUrl}
                                        posterPlan={wantAdCreatedUserPlan}
                                        shareStatus={shareLinkStatus}
                                        onRetryShare={retryShareLinks}
                                      />
                                    </div>
                                  )}

                                </div>

                              </>
                            )}
                          </WantListingRail>
                        )}
                      </div>
                    </Tab>
                    <Tab
                      eventKey={"Matches"}
                      disabled={isMatchingInProgress}
                      title={
                        isMatchingInProgress ? (
                          <span
                            className="matches-matching-label"
                            aria-label="Potential matches are being calculated and will appear here automatically"
                            title="Potential matches are being calculated and will appear here automatically"
                          >
                            Matches{" "}
                            <span
                              className="matches-matching-wave"
                              aria-hidden="true"
                            >
                              <span />
                              <span />
                              <span />
                            </span>
                          </span>
                        ) : tourWantAd ? (
                          matchesTabTitle(tourWantAd.tourMatchCount || 0)
                        ) : !adDetails?.wantAdId || isMatchesLoading ? (
                          <>
                            Matches{" "}
                            <span
                              className="mls-inline-spinner"
                              aria-hidden="true"
                            />
                          </>
                        ) : (
                          matchesTabTitle(totalMlsMatches)
                        )
                      }
                    >
                      {renderMatchesTab()}
                    </Tab>
                  </Tabs>
                </div>
              </>
            )}
          </PageWrapper>

          {!noWantAdFound &&
            showShareModal &&
            (loggedInUserId || shareUrl) &&
            !shareLinksQuery.isError && (
            <ShareYourAdModal
              show={showShareModal}
              onHide={handleClose}
              adDetails={adDetails}
              isWantAdCreated={isWantAdCreated}
              shareLink={shareUrl}
              imageLink={imageUrl}
              fbLink={fbUrl}
              fromWantAdDetails={true}
            />
          )}

          <ShareMyWantAdSmallModal
            show={showSuccessModal}
            onHide={handleSuccessModalClose}
            showShareModalPopup={showShareModalPopup}
          />
          <NoWantAdFoundModal show={noWantAdFound} />
          <ReportWantAdModal
            show={showReportModal}
            wantAdId={wantAdId}
            onHide={() => handleReportPopup(false)}
          />
          {introDetails.length > 0 && (
            <ViewIntroductionModal
              details={introDetails?.[0]}
              show={showViewIntroductionModal}
              onHide={handleIntroDetailModal}
            />
          )}
          {/*  Add to client favorite Modal */}
          <AddToClientWantAdModal
            show={modalShow}
            showAddToClientWantAdModal={() => setModalShow(true)}
            onHide={() => setModalShow(false)}
            data={results}
            handleSkip={() =>
              handleFavoriteOrUnfavoriteWantAd(isFavourite, wantAdId)
            }
            handleConfirm={handleClientFavoriteWantAd}
            searchText={searchText}
            onChangeText={(e) => onChangeText(e)}
            onAddClient={() =>
              queryClient.invalidateQueries({
                queryKey: ["wantAdAgentClients"],
              })
            }
          />
          <DeleteModal
            show={unsyndicateModalShow}
            onHide={() => setUnsyndicateModalShow(false)}
            title={"Stop syndicating this Want-Listing?"}
            message={
              "Its public page and share links stop working, and it goes back to being visible to members only. You can syndicate it again at any time."
            }
            buttonText={"Stop Syndicating"}
            handleConfirm={() => {
              setUnsyndicateModalShow(false);
              unsyndicateWantAdMutation.mutate(wantAdId);
            }}
          />
          <CloseOutSheet
            show={closeOutShow}
            wantAd={{ wantAdId, title: adDetails?.adTitle }}
            onHide={() => setCloseOutShow(false)}
            onRemoveWithoutOutcome={() => {
              setCloseOutShow(false);
              setDeleteModalShow(true);
            }}
            onClosedOut={() =>
              queryClient.invalidateQueries({ queryKey: ["wantAdDetails"] })
            }
          />
          <DeleteModal
            show={deleteModalShow}
            onHide={() => setDeleteModalShow(false)}
            title={"Are you sure you want to permanently delete your Want-Listing?"}
            message={
              "Deleting this ad will stop any further introductions from being sent to you."
            }
            buttonText={" Delete Want-Listing"}
            handleConfirm={() => handleDeleteWantAd()}
          />
          <ArchiveModal
            show={archiveModalShow}
            onHide={() => setArchiveModalShow(false)}
            handleConfirm={() => handleArchiveWantAd()}
          />
          <AgentProfileViewModal
            show={showAgentProfileViewModal}
            onClose={agentProfileViewModalClose}
            userId={_.get(adDetails, "userId", "")}
            isTitleShow={true}
          />
          <PremiumPlanModal
            userDetails={userDetailsFromStore[0]}
            show={showPremiumPlanModal}
            onHide={() => {
              PremiumPlanModalClose();
              setProReasonText(null);
            }}
            showPlanModal={PremiumPlanModalShow}
            reasonText={proReasonText}
          />
          <SyndicateListingModal
            show={showSyndicateModal}
            onHide={() => setShowSyndicateModal(false)}
            syndicateListing={() => handleSyndicateWantAd(_.get(adDetails, "wantAdId", ""))}
            listing={adDetails}
          />
          <GitchaShareAccess
            show={!!shareAccessModal}
            initialAudience={shareAccessModal || "members"}
            onHide={() => {
              shareAccessModalRef.current = null;
              setShareAccessModal(null);
            }}
            isSyndicated={!!isSyndicatedToPublic}
            memberUrl={typeof window !== "undefined" ? window.location.href : ""}
            wantAdId={wantAdId}
            adDetails={adDetails}
            shareUrl={shareUrl}
            fbUrl={fbUrl}
            imageUrl={imageUrl}
            isSyndicating={syndicateWantAdMutation.isPending}
            onSyndicate={() => handleSyndicateWantAd(_.get(adDetails, "wantAdId", ""))}
          />
          <InventorySuccessModal
            show={showSyndicateSuccessModal}
            onHide={() => setSyndicateSuccessModal(false)}
            successPopUpTitle={"Want-Listing syndicated successfully"}
            successPopUpDesc={"Your Want-Listing has been syndicated to Gitcha."}
          />
          <MarketRestrictedModal
            show={!!marketRestrictedReason}
            reason={marketRestrictedReason}
            onHide={() => setMarketRestrictedReason(null)}
          />
          <MatchesFeatureModal
            show={showMatchesFeatureModal}
            onHide={() => setShowMatchesFeatureModal(false)}
          />
        </>
      )}
    </>
  );
};

export default WantadDetails;
